<script lang="ts">
	import { inputFieldFormats } from '$utils/formatters'
	import Card from '../atoms/Card.svelte'
	import InputField from '../molecules/InputField.svelte'
	import type { Offer, OfferDesignatedEntity } from './types/offer'
	import { t } from '$i18n/i18n'
	import { createEventDispatcher, getContext, onMount, setContext } from 'svelte'
	import AutocompleteInputField from '../autocomplete/AutocompleteInputField.svelte'
	import {
		type AutocompleteFieldConfig,
		getFormAcConfigFetch
	} from '$types/autocompleteFieldConfig'
	import type { PubPageParams } from './types/pubPageParams'
	import { type AutocompletePop, NO_POP_SELECTION_ID } from '$types/autocompletePop'
	import { addressDataToString, DefaultAddressData } from '$types/addressData'
	import { RequestedEntityType } from './types/requestDto'
	import { userIsLoggedIn } from '../stores'
	import { fetchUtils } from '$utils/fetchUtils'
	import type { Writable } from 'svelte/store'

	const dispatch = createEventDispatcher()
	setContext('useMaterialDesign', true)
	const urlParams: PubPageParams = getContext('urlParams') || null
	const offerDesignatedEntity: Writable<OfferDesignatedEntity | null> =
		getContext('offerDesignatedEntity')
	const url: string = $userIsLoggedIn ? 'popsForOptimizer' : 'public/popsForOptimizer'
	const fetchSuffix = urlParams
		? `&requestPublicId=${urlParams.requestPublicId}&offerUserPublicId=${urlParams.offerUserPublicId ?? null}&requestedEntityType=${urlParams.requestedEntityType}&requestedEntityId=${urlParams.requestedEntityId}`
		: ''

	export let offer: Offer

	let popConfig: AutocompleteFieldConfig = getFormAcConfigFetch(
		url,
		false,
		'id',
		'text',
		false,
		false,
		fetchSuffix
	)
	popConfig.minQuery = 0
	let popOptions: AutocompletePop[] = []

	/**
	 * designatedEntity holds the full selected POP.
	 * Populated by onChange on user select (svelecte passes objectSelection to onChange
	 * per Svelecte.svelte:702 regardless of valueAsObject) or by the onMount imperative
	 * prefill below. entityId stays string-typed via bind:value (no valueAsObject) to
	 * avoid the svelecte v5 refetchWith + valueAsObject seeding quirk where a string id
	 * passes through Object.fromEntries(Object.entries(val)) and produces a garbage
	 * selection object (see Svelecte.svelte:520-543).
	 */
	let designatedEntity: AutocompletePop | null = null
	/**
	 * only seed from the persisted offer location — never fall back to offerDesignatedEntity which is the
	 * requested entity (lot or pop the offer is FOR), not the user-selected Standort
	 */
	let entityId: string | null = offer.offerLocationId || null
	/**
	 * Sync hasMaterialProfile from the server-loaded offer dto whenever it changes (= after fetchRequestCollecting).
	 * Using a function so the tracking variable assignment doesn't trigger no-useless-assignment.
	 * Does NOT fire on interactive pop selection (onPopSelect mutates offer directly without store update).
	 */
	let _hasMaterialProfileSynced: boolean | undefined = undefined
	function _syncHasMaterialProfile(val: boolean | undefined) {
		if (val === _hasMaterialProfileSynced) return
		_hasMaterialProfileSynced = val
		/**
		 * OfferFactsForm reads the designated entity to decide the label of the createPopMaterial
		 * checkbox, so the hasMaterialProfile flag from the server-loaded offer has to be copied
		 * onto the entity. only a POP (Standort) can have a material profile, therefore the flag
		 * is merged onto POP entities only. a LOT entity (the requested lot the offer is made for)
		 * never receives the flag; for LOTs the checkbox label reads the offer DTO directly
		 */
		if ($offerDesignatedEntity?.type === RequestedEntityType.POP) {
			$offerDesignatedEntity = { ...$offerDesignatedEntity, hasMaterialProfile: val ?? false }
		}
	}
	$: _syncHasMaterialProfile(offer.hasMaterialProfile)

	/**
	 * INITIAL_FETCH triggers the backend "init" contract: the authed endpoint only returns the
	 * candidate pop list when qry=init AND init is non-empty (OptimizerApiController); the public
	 * endpoint ignores both and resolves the pop from requestedEntityId in fetchSuffix.
	 */
	const INITIAL_FETCH = 'INITIAL_FETCH'

	async function fetchInitialPops(): Promise<AutocompletePop[] | null> {
		return (await fetchUtils.get(
			`/api/${url}/autocomplete/svelte?qry=init&init=${INITIAL_FETCH}${fetchSuffix}`,
			false
		)) as AutocompletePop[] | null
	}

	function applyPop(pop: AutocompletePop) {
		designatedEntity = pop
		entityId = pop.id
		onPopSelect(false)
	}

	onMount(async () => {
		/**
		 * preload the option list so svelecte can render the dropdown AND resolve the selected
		 * pop's label. without options present when the value is set, svelecte creates a bare item
		 * labelled with the raw id (see Svelecte.svelte:530-542)
		 */
		let pops: AutocompletePop[] = []
		try {
			const fetched = await fetchInitialPops()
			if (Array.isArray(fetched)) pops = fetched
		} catch (error) {
			/**
			 * best-effort preload: the form keeps working without options (free-text address). an
			 * anonymous pub-page visitor can neither fix nor act on this failure, so the error toast
			 * is reserved for logged-in users
			 */
			if ($userIsLoggedIn) {
				fetchUtils.catchErrorAndShowNotification()(error)
			}
			// fall through — popOptions stays empty
		}
		popOptions = pops

		/**
		 * only default to the URL's requested pop for a brand-new offer. an existing offer
		 * (publicId set) that was saved without a Standort must stay empty, not re-prefill the pop.
		 */
		const isNewOffer = !offer.publicId
		const isPOPUrlPrefill =
			isNewOffer &&
			urlParams?.requestedEntityId &&
			urlParams?.requestedEntityType === RequestedEntityType.POP
		let pop: AutocompletePop | undefined
		if (isPOPUrlPrefill) {
			const seedId = $offerDesignatedEntity?.id || urlParams.requestedEntityId
			pop = pops.find((p) => p.id === seedId)
		} else if (offer.offerLocationId || offer.offerLocation?.trim()) {
			// edit case: resolve pop from persisted id (new offers) or address string (old offers without stored id)
			pop = offer.offerLocationId
				? pops.find((p) => p.id === offer.offerLocationId)
				: pops.find((p) => addressDataToString(p.address) === offer.offerLocation)
		}
		if (pop) {
			applyPop(pop)
		}
	})

	const editOffer = () => {
		dispatch('editOffer')
	}

	function onPopSelect(shouldSendEvent: boolean = true) {
		if (!entityId || entityId === NO_POP_SELECTION_ID) {
			if ($offerDesignatedEntity) {
				$offerDesignatedEntity = null
			}
			offer.offerLocation = ''
			offer.offerLocationId = ''
		} else if (designatedEntity) {
			$offerDesignatedEntity = {
				type: RequestedEntityType.POP,
				id: entityId,
				label: designatedEntity.name ?? '',
				hasMaterialProfile: designatedEntity.hasMaterialProfile ?? false
			}
			offer.offerLocation = addressDataToString(designatedEntity.address ?? DefaultAddressData())
			offer.offerLocationId = designatedEntity.id
		}
		// else: selection id present but pop not hydrated (edit) — keep already-loaded offer.offerLocation / offer.offerLocationId

		/**
		 * we can prevent sending the event, e.g. when we prefill the pop and the location by url,
		 * since this would trigger a selection of the offer. If we do not send the event, the material selection is not triggered
		 */
		if (shouldSendEvent) {
			editOffer()
		}
	}

	function onPopChange(selection: string | number | object | unknown[] | null | undefined) {
		/**
		 * svelecte passes objectSelection to onChange regardless of valueAsObject
		 * (Svelecte.svelte:702). single-select → single object or null.
		 */
		const raw = Array.isArray(selection) ? (selection[0] ?? null) : (selection ?? null)
		const next = raw && typeof raw === 'object' ? (raw as AutocompletePop) : null
		designatedEntity = next
		entityId = next?.id ?? null
		onPopSelect()
	}
</script>

<Card borderRadius="0.625rem" boxShadow="">
	<!-- to unset, boxShadow needs to be empty -->
	<div class="offer-facts-place-container">
		<div class="offerSitePop">
			<AutocompleteInputField
				id="exclPop"
				bind:value={entityId}
				options={popOptions}
				autocompleteConfig={popConfig}
				label={$t('UI.counterOffer.acceptPop.label')}
				placeholder={$t('UI.counterOffer.acceptPop.ph')}
				helperText={$t('UI.counterOffer.acceptPop.helperText')}
				hideDropdownArrow={false}
				disabled={!entityId && !!offer.offerLocation?.trim()}
				onChange={onPopChange}
				onBlur={onPopSelect}
			/>
		</div>
		<div class="offerSiteLocation">
			<InputField
				id="exclPlace"
				bind:value={offer.offerLocation}
				format={inputFieldFormats.FULL}
				label={$t('UI.counterOffer.acceptPlace.label')}
				helperText={$t('UI.counterOffer.acceptPlace.helperText')}
				placeholder={$t('UI.counterOffer.acceptPlace.ph')}
				readOnly={!!(entityId && entityId !== NO_POP_SELECTION_ID)}
				clearable
				on:change={editOffer}
			/>
		</div>
	</div>
</Card>

<style lang="scss">
	@use '$styles/global';

	.offer-facts-place-container {
		width: 100%;
		padding: 1.25rem;
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1.375rem 2rem;
		align-items: start;
	}

	.offerSitePop {
		grid-column: 1 / 1;
		display: inherit;
	}

	.offerSiteLocation {
		grid-column: 2 / 2;
		display: inherit;
	}

	@media screen and (max-width: global.$small) {
		.offer-facts-place-container {
			grid-template-columns: 1fr;
		}

		.offerSitePop {
			grid-column: 1 / 1;
		}

		.offerSiteLocation {
			grid-column: 1 / 1;
		}
	}
</style>
